import IconColumns from '@assets/icons/columns.svg?react';
import IconDefault from '@assets/icons/default.svg?react';
import IconDelete from '@assets/icons/delete.svg?react';
import IconExcel from '@assets/icons/excel.svg?react';
import IconFilters from '@assets/icons/filters.svg?react';
import IconLock from '@assets/icons/lock.svg?react';
import IconPlus from '@assets/icons/plus.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
 Badge,
 Box,
 Button,
 Grid2,
 IconButton,
 Theme,
 Typography,
} from '@mui/material';
import { GridSlots, PropsFromSlot } from '@mui/x-data-grid-premium';

import useGridToolbar from './hooks/useGridToolbar';
import ToolbarReports from './ToolbarReports';
import RoleChecker from '../RoleChecker';

interface CustomCalendarHeaderProps
 extends PropsFromSlot<GridSlots['toolbar']> {}

const btnTransition =
 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms';

const GridToolbar = ({}: CustomCalendarHeaderProps) => {
 const {
  t,
  th,
  pageName,
  loading,
  apiCurrent,
  rowCount,
  selectedRows,
  filtersCount,
  isCompactDensity,
  currentPresetForPage,
  isPreset,
  handleGetExcel,
  handleDeleteItems,
  handleBlockResults,
  handleResetPreset,
  handleSavePreset,
 } = useGridToolbar();

 return (
  <Box
   sx={{
    px: '20px',
    py: { xs: '16px', md: isCompactDensity ? '12px' : '16px' },
   }}
  >
   <Grid2 container spacing={2} sx={{}}>
    <Grid2
     size={{ sm: 6, xl: 4 }}
     sx={{ display: 'flex', height: '40px', gap: 2 }}
    >
     <Box
      sx={{
       display: { xs: 'flex', md: 'none', alignItems: 'center', gap: '12px' },
      }}
     >
      <RoleChecker resource={pageName} action="Create">
       <IconButton onClick={() => apiCurrent.setCreateModalOpen(true)}>
        <IconPlus
         style={{
          color: th.palette.kemek.blue,
          transition: btnTransition,
         }}
        />
       </IconButton>
      </RoleChecker>
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
       {t(`entities.${pageName}`)} ({rowCount})
      </Typography>
     </Box>
     <RoleChecker resource={pageName} action="Create">
      <Button
       variant="outlined"
       sx={{
        display: { xs: 'none', md: 'flex' },
        ...(isCompactDensity && {
         minWidth: '40px',
         p: 1,
         '.MuiButton-endIcon': { m: 0 },
        }),
       }}
       endIcon={
        <IconPlus
         style={{
          color: th.palette.kemek.blue,
          transition: btnTransition,
         }}
        />
       }
       onClick={() => apiCurrent.setCreateModalOpen(true)}
      >
       {!isCompactDensity && t('gridToolbar.create')}
      </Button>
     </RoleChecker>
     <RoleChecker resource={pageName} action="Delete">
      <Button
       variant="outlined"
       color="error"
       sx={{
        display: { xs: 'none', md: 'flex' },
        ...(isCompactDensity && {
         minWidth: '40px',
         p: 1,
         '.MuiButton-endIcon': { m: 0 },
        }),
       }}
       endIcon={
        !selectedRows?.length ? (
         <IconDelete
          style={{
           color: th.palette.kemek.lightGray,
           transition: btnTransition,
          }}
         />
        ) : (
         <IconDelete
          style={{
           color:
            th.palette.mode === 'dark'
             ? th.palette.kemek.lightRed
             : th.palette.kemek.red,
           transition: btnTransition,
          }}
         />
        )
       }
       onClick={handleDeleteItems}
       disabled={!selectedRows?.length}
      >
       {!isCompactDensity && t('global.delete')}
      </Button>
     </RoleChecker>
     {pageName === 'results' && (
      <RoleChecker resource={pageName} action="Approve">
       <Button
        variant="outlined"
        color="secondary"
        sx={{
         display: { xs: 'none', md: 'flex' },
         ...(isCompactDensity && {
          minWidth: '40px',
          p: 1,
          '.MuiButton-endIcon': { m: 0 },
         }),
        }}
        endIcon={
         <IconLock
          style={{
           color: !selectedRows?.length
            ? th.palette.kemek.lightGray
            : th.palette.kemek.darkGray,
           transition: btnTransition,
          }}
         />
        }
        onClick={handleBlockResults}
        disabled={!selectedRows?.length}
       >
        {!isCompactDensity && t('global.block')}
       </Button>
      </RoleChecker>
     )}
    </Grid2>
    <Grid2
     size={{ sm: 6, xl: 4 }}
     offset={{ sm: 0, xl: 4 }}
     sx={{
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      justifyContent: 'flex-end',
      gap: '20px',
      ml: 'auto',
     }}
    >
     {pageName === 'results' && <ToolbarReports />}
     <Button
      size="small"
      variant="outlined"
      endIcon={<IconExcel />}
      onClick={handleGetExcel}
      disabled={!!loading?.getTableExcel}
      sx={{
       display: { xs: 'none', md: 'flex' },
       p: 1,
       text: 'red',
       minWidth: '40px',
       '.MuiButton-icon': { m: 0 },
      }}
     />
     {/* filter btn */}
     <IconButton
      aria-label=""
      size="small"
      onClick={() => {
       apiCurrent.setFilterSidebarOpen((prev) => !prev);
      }}
      sx={{ width: 24, height: 24, p: '3px' }}
     >
      {filtersCount > 0 ? (
       <Badge badgeContent={filtersCount} color="primary">
        <IconFilters
         style={{ width: 20, height: 18, color: th.palette.kemek.darkestGray }}
        />
       </Badge>
      ) : (
       <IconFilters
        style={{ width: 20, height: 18, color: th.palette.kemek.darkestGray }}
       />
      )}
     </IconButton>
     {/* preset */}
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       gap: '20px',
       backgroundColor: (theme: Theme) =>
        isPreset ? theme.palette.kemek.lightBlue : 'transparent',
       borderRadius: isPreset ? '20px' : '0',
       px: isPreset ? 2 : 0,
       py: 1,
       transition: btnTransition,
      }}
     >
      <IconButton
       aria-label=""
       size="small"
       onClick={() => apiCurrent.setColModalOpen(true)}
       sx={{ width: 24, height: 24, p: '3px' }}
      >
       {isPreset ? (
        <Badge badgeContent="!" color="primary">
         <IconColumns
          style={{ width: 18, height: 18, color: th.palette.kemek.darkestGray }}
         />
        </Badge>
       ) : (
        <IconColumns
         style={{ width: 18, height: 18, color: th.palette.kemek.darkestGray }}
        />
       )}
      </IconButton>
      {isPreset && (
       <>
        <IconButton
         disabled={!currentPresetForPage}
         aria-label="revert"
         onClick={handleResetPreset}
         sx={() => ({
          width: 24,
          height: 24,
          opacity: !currentPresetForPage ? 0.5 : 1,
         })}
        >
         <IconDefault
          style={{
           color:
            th.palette.kemek[!currentPresetForPage ? 'gray' : 'darkestGray'],
          }}
         />
        </IconButton>
        <IconButton
         disabled={!currentPresetForPage}
         aria-label="save"
         onClick={handleSavePreset}
         sx={() => ({
          width: 24,
          height: 24,
          opacity: !currentPresetForPage ? 0.5 : 1,
         })}
        >
         <IconSave
          style={{
           color: th.palette.kemek[!currentPresetForPage ? 'gray' : 'blue'],
          }}
         />
        </IconButton>
       </>
      )}
     </Box>
    </Grid2>
   </Grid2>
  </Box>
 );
};
GridToolbar.displayName = 'GridToolbar';
export default GridToolbar;

